import { useEffect, useState } from 'react';
import { apiGet, apiPost, apiPut } from '../../services/apiClient';

const emptyPolicy = {
  name: '',
  policy_type: 'cancellation',
  summary: '',
  content: '',
  sort_order: 0,
  is_default: false,
  is_active: true,
};

const getPolicyContentText = (content) => {
  if (!content) return '';
  if (typeof content === 'string') return content;
  if (typeof content?.body === 'string') return content.body;
  try {
    return JSON.stringify(content, null, 2);
  } catch (_err) {
    return '';
  }
};

const AdminPolicies = ({ token }) => {
  const [policies, setPolicies] = useState([]);
  const [form, setForm] = useState(emptyPolicy);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiGet('/api/admin/policy-templates', token);
      setPolicies(res || []);
    } catch (err) {
      setError(err.message || 'Failed to load policy templates');
      setPolicies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = () => {
    setEditingId(null);
    setForm(emptyPolicy);
  };

  const save = async () => {
    if (!form.name) return;
    setSaving(true);
    setError('');
    const payload = {
      ...form,
      sort_order: Number(form.sort_order || 0),
      content: form.content ? { body: form.content } : null,
    };
    try {
      if (editingId) {
        await apiPut(`/api/admin/policy-templates/${editingId}`, payload, token);
      } else {
        await apiPost('/api/admin/policy-templates', payload, token);
      }
      await load();
      reset();
    } catch (err) {
      setError(err.message || 'Failed to save policy template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Properties</p>
          <h1 className="admin-page-title text-xl font-semibold text-slate-900">Policies templates</h1>
        </div>
      </div>
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="admin-card bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-3">
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label text-sm">Name</label>
            <input
              className="form-control"
              value={form.name}
              onChange={(e) => setForm((v) => ({ ...v, name: e.target.value }))}
              placeholder="Flexible cancellation"
            />
          </div>
          <div className="col-md-3">
            <label className="form-label text-sm">Type</label>
            <select
              className="form-select"
              value={form.policy_type}
              onChange={(e) => setForm((v) => ({ ...v, policy_type: e.target.value }))}
            >
              <option value="cancellation">Cancellation</option>
              <option value="house_rules">House rules</option>
              <option value="refund">Refund</option>
              <option value="pet">Pet</option>
              <option value="smoking">Smoking</option>
              <option value="id_requirement">ID requirement</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label text-sm">Sort</label>
            <input
              type="number"
              className="form-control"
              value={form.sort_order}
              onChange={(e) => setForm((v) => ({ ...v, sort_order: Number(e.target.value) }))}
            />
          </div>
          <div className="col-md-12">
            <label className="form-label text-sm">Summary</label>
            <textarea
              className="form-control"
              rows={2}
              value={form.summary}
              onChange={(e) => setForm((v) => ({ ...v, summary: e.target.value }))}
              placeholder="Guests can cancel up to 24 hours before check-in for a full refund."
            />
          </div>
          <div className="col-md-12">
            <label className="form-label text-sm">Detailed policy</label>
            <textarea
              className="form-control"
              rows={4}
              value={form.content}
              onChange={(e) => setForm((v) => ({ ...v, content: e.target.value }))}
              placeholder="Write the full policy content. This will be stored and reused as a template."
            />
          </div>
          <div className="col-md-6 d-flex align-items-center gap-2">
            <input
              type="checkbox"
              className="form-check-input"
              id="policy-active"
              checked={form.is_active}
              onChange={(e) => setForm((v) => ({ ...v, is_active: e.target.checked }))}
            />
            <label htmlFor="policy-active" className="form-check-label text-sm">
              Active
            </label>
          </div>
          <div className="col-md-6 d-flex align-items-center gap-2">
            <input
              type="checkbox"
              className="form-check-input"
              id="policy-default"
              checked={form.is_default}
              onChange={(e) => setForm((v) => ({ ...v, is_default: e.target.checked }))}
            />
            <label htmlFor="policy-default" className="form-check-label text-sm">
              Mark as default for new listings
            </label>
          </div>
          <div className="col-12 d-flex gap-2">
            <button className="btn btn-primary" disabled={saving} onClick={save}>
              {saving ? 'Saving...' : editingId ? 'Update policy' : 'Add policy'}
            </button>
            {editingId && (
              <button className="btn btn-outline-secondary" onClick={reset}>
                Cancel
              </button>
            )}
          </div>
        </div>

        <div className="border rounded-lg overflow-hidden">
          <table className="table table-sm align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Sort</th>
                <th>Status</th>
                <th>Default</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="text-center text-muted">
                    Loading...
                  </td>
                </tr>
              )}
              {!loading && !policies.length && (
                <tr>
                  <td colSpan={6} className="text-center text-muted">
                    No policy templates yet
                  </td>
                </tr>
              )}
              {policies.map((p) => (
                <tr key={p.id}>
                  <td className="fw-semibold">{p.name}</td>
                  <td className="text-muted small text-uppercase">{p.policy_type}</td>
                  <td>{p.sort_order ?? 0}</td>
                  <td>
                    <span className={`badge ${p.is_active ? 'bg-success-subtle text-success' : 'bg-secondary'}`}>
                      {p.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{p.is_default ? 'Yes' : 'No'}</td>
                  <td className="text-end">
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => {
                        setEditingId(p.id);
                        setForm({
                          name: p.name || '',
                          policy_type: p.policy_type || 'cancellation',
                          summary: p.summary || '',
                          content: getPolicyContentText(p.content),
                          sort_order: p.sort_order || 0,
                          is_default: p.is_default ?? false,
                          is_active: p.is_active ?? true,
                        });
                      }}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminPolicies;
